// ---------------------------------------------------------------------------
// industry.ja.ts — Japanese translations for all 5 industry landing pages
// ---------------------------------------------------------------------------
import type { IndustryText } from './industry.content';

export const INDUSTRY_JA: Record<string, IndustryText> = {
  'hotel-software-for-small-hotels': {
    propertyType: '小規模ホテルオーナー',
    heroTitle: '大きな志を持つ小規模ホテルのためのホテルソフトウェア',
    heroSubtitle: '10〜50室向けAI搭載クラウドPMS。AIダイナミックプライシング、スマートハウスキーピング、AIコンシェルジュが小規模ホテルに大手チェーンと同等のインテリジェンスを提供 — ITチーム不要。',
    painPoints: [
      { title: 'あらゆる場所で手作業', description: 'ノート、スプレッドシート、手書き記録がエラー、予約の見落とし、無駄な時間を生みます。' },
      { title: '高額な旧システム', description: 'エンタープライズPMSは年間数千ドルかかり、小規模ホテルには負担できない専任ITチームが必要です。' },
      { title: '社内技術チームなし', description: '技術チームがなければ、ソフトウェアの問題解決は高額なコンサルタントと長いダウンタイムを意味します。' },
      { title: 'OTAへの過度な依存', description: 'すべてのOTA予約に15-25%の手数料を支払うことで、すでに薄い利益率がさらに圧迫されます。' },
    ],
    solutionFeatures: [
      { title: 'シンプルで強力なPMS', description: 'ドラッグ＆ドロップのチェックイン、直接予約、リアルタイム空室状況を備えたルームボード。トレーニング不要。', highlight: 'トレーニング不要' },
      { title: '自動ハウスキーピング', description: 'チェックアウト時の自動タスク割り当て、リアルタイムの部屋ステータス追跡、紙のハウスキーピングシート廃止。' },
      { title: '直接予約エンジン', description: 'ウェブサイト用の手数料なし予約ウィジェット。直接支払いを受け付け、ゲストデータベースを構築。' },
      { title: '基本的な収益ツール', description: '稼働率とRevPARのシンプルなダッシュボード。季節料金と最低滞在ルールを設定。' },
      { title: 'モバイルアクセス', description: 'どこからでも施設を管理。スマートフォンから部屋の状況確認、予約承認、レポート閲覧が可能。' },
      { title: 'クラウドで常時稼働', description: 'サーバールーム不要、インストール不要、メンテナンス不要。自動アップデートと99.9% SLA。' },
      { title: 'AI搭載インテリジェンス', description: 'AIダイナミックプライシングが需要に応じて料金を調整、AIコンシェルジュが24時間ゲスト対応、スマートハウスキーピングが自動で客室を割り当て。' },
    ],
    caseStudy: { challenge: 'ノートとExcelですべてを管理していた30室のブティックホテル。頻繁なダブルブッキング、稼働率トレンドの把握不能、予約の90%が20%手数料のOTA経由。', solution: 'HotelStack.io StarterプランをPMS、直接予約エンジン、OTAチャネル同期で導入。スタッフトレーニング込みで36時間で完全移行。', results: ['リアルタイムOTA同期によるダブルブッキングの完全解消', '直接予約が6ヶ月で10%から40%に増加', 'チェックイン時間を8分から2分に短縮', '手作業記録に費やす時間を70%以上削減'] },
    statsLabels: ['時間削減', '直接予約増加', 'セットアップ時間', '開始価格'],
    testimonial: { quote: 'ノートから完全デジタルホテルに2日で移行しました。予約エンジンだけで昨年OTA手数料を数千ドル節約できました。今までで最高の投資です。', role: 'オーナー', hotel: 'The Harbour Inn、リスボン' },
    faqItems: [
      { question: 'HotelStack.ioは小規模ホテルにとって手頃ですか？', answer: 'はい。プランは最大50室で月49ドルから。セットアップ費用なし、隠れた料金なし、14日間無料トライアル。' },
      { question: 'HotelStack.ioの利用にITチームは必要ですか？', answer: 'まったく必要ありません。HotelStack.ioは技術者でないホテリエのために設計されたクラウドプラットフォームです。オンボーディングチームがすべて対応します。' },
      { question: 'スマートフォンからホテルを管理できますか？', answer: 'はい。プラットフォームは完全にレスポンシブで、あらゆるデバイスで動作します。' },
      { question: '直接予約はどのくらい早く始まりますか？', answer: 'BookMyRoom.ioの直接予約エンジンは登録から48時間以内に稼働します。' },
    ],
    relatedIndustries: [
      { label: 'ブティックホテル', description: 'ユニークな施設向けのパーソナライズされたゲスト体験' },
      { label: 'ホステル', description: 'バジェット宿泊施設向けのベッドレベル在庫管理' },
      { label: 'ホテルチェーン', description: '大規模なマルチプロパティ管理' },
      { label: 'リゾート', description: 'F&B、スパ、アクティビティ管理' },
    ],
  },
  'hotel-software-for-resorts': {
    propertyType: 'リゾート＆スパ運営者',
    heroTitle: 'ワールドクラスの体験を提供するリゾート管理ソフトウェア',
    heroSubtitle: '客室、レストラン、スパ、アクティビティのAI搭載リゾート管理。AI需要予測が季節料金を最適化、スマートハウスキーピングがゾーンを効率的に割り当て、AIコンシェルジュがアウトレット横断でゲスト対応。',
    painPoints: [
      { title: '複雑なF&B運営', description: '複数のレストラン、ルームサービス、プールバー、バンケットが連携しない別々のシステムを必要とします。' },
      { title: 'スパ＆アクティビティのスケジューリング', description: 'スパ予約、アクティビティスケジュール、機器レンタルを手動で管理すると、ダブルブッキングと収益損失が発生します。' },
      { title: 'アクティビティの調整', description: '大規模施設でのツアー、ウェルネスプログラム、エンターテイメントの調整は、適切なツールなしではロジスティクスの悪夢です。' },
      { title: '季節的な需要変動', description: 'リゾートは極端な季節性に直面します。ダイナミックプライシングなしでは、ピーク時に収益を逃すか、閑散期に空室のままになります。' },
    ],
    solutionFeatures: [
      { title: 'POS＆F&Bシステム', description: 'レストラン、バー、ルームサービス向けの完全POS。メニュー管理、KOT追跡、テーブルレイアウト。', highlight: 'マルチアウトレット' },
      { title: 'レストラン管理', description: '複数のダイニング施設の予約を管理。食事プラン、食事制限、バンケットを一画面で。' },
      { title: 'アクティビティスケジューリング', description: 'リゾートアクティビティ、スパトリートメント、ツアーのスケジュール管理。ゲストアカウントとの自動同期。' },
      { title: 'ダイナミック季節料金', description: '季節ごとの自動料金ルール。収益管理ツールが年間を通じてRevPARを最大化。' },
      { title: '団体予約エンジン', description: 'ウェディング、会議、団体ツアーをルームリスト、キャンセル追跡、統合請求で管理。' },
      { title: '大規模ハウスキーピング', description: '大規模施設向けのゾーンベースのハウスキーピング割り当て。リネン追跡、チェックリスト、タスクステータス。' },
      { title: 'AI需要予測と料金設定', description: 'ML駆動の需要予測が90日先の稼働率を予測。AIダイナミックプライシングがシーズン、イベント、競合変動に応じて料金を自動調整しRevPARを最大化。' },
    ],
    caseStudy: { challenge: '3つのレストラン、スパ、プールバーを持つ120室の高級リゾート。別々のPOSシステムが未請求料金を発生させ、手動のスパスケジューリングがダブルブッキングを引き起こしていた。', solution: 'HotelStack.io Enterpriseを全アウトレット統合POS、統合スパスケジューリング、ダイナミック季節料金ルールで導入。', results: ['未請求料金の排除によりF&B収益25%増加', 'ハウスキーピングサイクル30%改善', '自動季節料金でRevPAR 15%増加', '統合請求でゲスト満足度98%達成'] },
    statsLabels: ['F&B収益増加', 'ハウスキーピング改善', 'RevPAR増加', 'ゲスト満足度'],
    testimonial: { quote: '3つのレストランとスパの管理に4つの異なるシステムが必要でした。今はすべてが自動的に一つのゲストアカウントに流れます。未請求料金はゼロになりました。', role: 'リゾートディレクター', hotel: 'Azure Coast Resort、プーケット' },
    faqItems: [
      { question: 'HotelStack.ioは複数のレストランを管理できますか？', answer: 'はい。POSシステムは無制限のアウトレットをサポート。各アウトレットには独自のメニューとテーブルレイアウトがありますが、すべての料金は統合ゲストアカウントに同期します。' },
      { question: 'スパやアクティビティの予約をサポートしますか？', answer: 'はい。アクティビティスケジューリングはスパトリートメント、ツアー、フィットネスクラス、機器レンタルを自動ゲストアカウント同期で管理します。' },
      { question: '季節料金はどのように機能しますか？', answer: '開始日と終了日で料金シーズンを定義。ピーク料金、閑散期割引、最低滞在要件の自動ルールを設定。' },
      { question: '団体予約やイベントを扱えますか？', answer: 'はい。団体予約モジュールはルームブロック、ルームリスト、キャンセル追跡、団体請求、イベント調整をサポートします。' },
    ],
    relatedIndustries: [
      { label: 'ブティックホテル', description: 'ヘリテージ施設向けのパーソナライズ体験' },
      { label: 'ホテルチェーン', description: 'リゾートグループ向けマルチプロパティ管理' },
      { label: '小規模ホテル', description: '小規模施設向け手頃なPMS' },
      { label: 'ホステル', description: 'バジェット宿泊施設管理ツール' },
    ],
  },
  'hotel-software-for-boutique-hotels': {
    propertyType: 'ブティック＆ヘリテージホテルオーナー',
    heroTitle: 'ブティックホテルのアイデンティティに合うホテルソフトウェア',
    heroSubtitle: 'ブティック規模でのAIパーソナライゼーション。AIゲストプロファイリングが好みを自動学習、AIセンチメント分析がレビューをリアルタイム監視、AIコンシェルジュが24時間カスタムサービスを提供。',
    painPoints: [
      { title: 'すべてのゲストがパーソナライズを期待', description: 'ブティックゲストは個人的なタッチにプレミアムを支払います。好みを追跡するCRMなしでは、期待される親密さを提供できません。' },
      { title: 'ヘリテージ建物の制約', description: 'ユニークな部屋構成と歴史建造物の制約により、標準的なPMSテンプレートが機能しません。' },
      { title: '小さなチーム、大きな期待', description: '限られたスタッフでは毎分が重要。コミュニケーション、ハウスキーピング、請求の手作業が貴重な時間を浪費します。' },
      { title: '非常に高いゲスト期待', description: 'ブティックゲストは詳細なレビューを残します。一つの見落とされた好みが慎重に築いた評判を傷つける可能性があります。' },
    ],
    solutionFeatures: [
      { title: '好みを記録するゲストCRM', description: '部屋の好み、食事要件、記念日、過去の滞在履歴を追跡。チェックイン時にメモを自動表示。', highlight: 'パーソナライズ' },
      { title: 'ロイヤルティプログラム', description: 'ポイント、報酬、限定オファー付きの独自のティアシステムを作成。一回限りの訪問者を生涯のアンバサダーに。' },
      { title: '自動コミュニケーション', description: '到着前メッセージ、滞在中のフォローアップ、チェックアウト後のレビュー依頼。ブランドの声に合ったテンプレート。' },
      { title: 'ブランド対応予約', description: '施設の美学に合ったホワイトラベル予約エンジン。カスタムカラー、フォント、画像。' },
      { title: '部屋別設定', description: '各部屋に固有のアメニティ、特徴、料金を定義。すべての部屋が異なるヘリテージ施設に最適。' },
      { title: 'レピュテーション分析', description: '全プラットフォームのレビュースコアを追跡。トレンドを特定し、フィードバックに対応し、改善の効果を測定。' },
      { title: 'AIコンシェルジュ＆プロファイリング', description: 'AIチャットボットが25以上の言語でゲスト対応。AIゲストプロファイリングが過去の滞在、レビュー、メッセージから好みを自動キャプチャ。' },
    ],
    caseStudy: { challenge: 'ユニークなテーマルームを持つ25室の改装ヴィラ。オーナーはゲストの好みを追跡できず、ロイヤルティプログラムなし、予約の65%がOTA経由。', solution: 'HotelStack.ioをゲストCRM、カスタムロイヤルティプログラム、自動コミュニケーション、ブランド予約エンジンで導入。', results: ['リピートゲストが1年で20%から50%に増加', '全プラットフォームで平均評価4.9を達成', '直接予約が35%から65%に増加', 'すべての直接予約でOTA手数料ゼロ'] },
    statsLabels: ['リピートゲスト', '平均評価', '直接予約', '直接予約手数料'],
    testimonial: { quote: 'ゲストは特別だと感じています。リピートゲストが到着し、お気に入りの部屋、枕のタイプ、好きなコーヒーをすでに知っていると、顔が輝きます。これがブティックホスピタリティの本質です。', role: 'オーナー兼ゼネラルマネージャー', hotel: 'Villa Toscana、フィレンツェ' },
    faqItems: [
      { question: '予約エンジンを自社ブランドにカスタマイズできますか？', answer: 'はい。ホワイトラベルエンジンはカスタムカラー、フォント、ロゴ、画像をサポートし、ウェブサイトと統合します。' },
      { question: 'CRMはパーソナライズにどう役立ちますか？', answer: 'CRMはゲストの好み、食事要件、部屋の選択、特別な機会を保存。スタッフはチェックイン時にこれらを自動的に確認できます。' },
      { question: 'ブティックホテル用のロイヤルティプログラムを作れますか？', answer: 'はい。ブランド独自のロイヤルティティアを作成し、ポイントルールを定義し、限定報酬を設定できます。' },
      { question: '異なる構成の部屋に対応しますか？', answer: 'もちろんです。各部屋に固有のアメニティ、ベッド構成、眺望、料金を定義できます。' },
    ],
    relatedIndustries: [
      { label: '小規模ホテル', description: '手頃なクラウドPMS' },
      { label: 'リゾート', description: '複雑なF&B＆アクティビティ管理' },
      { label: 'ホテルチェーン', description: 'マルチプロパティブランド管理' },
      { label: 'ホステル', description: 'バジェット管理ツール' },
    ],
  },
  'hotel-software-for-hostels': {
    propertyType: 'ホステル＆バジェット宿泊施設マネージャー',
    heroTitle: 'ベッドレベルの管理を備えたホステル管理ソフトウェア',
    heroSubtitle: 'ベッドレベル制御のAI搭載ホステルPMS。AIダイナミックプライシングがベッドあたり収益を最大化、AI需要予測がピーク期間を予測、スマート自動割り当てがドミトリーを効率的に充填。',
    painPoints: [
      { title: 'ベッドレベルの在庫ギャップ', description: 'ほとんどのPMSは部屋レベルの管理のみサポート。ホステルにはベッドレベルの追跡が必要です。' },
      { title: '高い回転率の混乱', description: '毎日ゲストが出入りする中、手動プロセスがフロントデスクでボトルネックを作ります。' },
      { title: '価格に敏感な旅行者', description: 'バジェット旅行者は数十のOTAで価格を比較します。即時の料金均一性なしでは予約を失います。' },
      { title: '長いチェックイン待ち列', description: 'グループで到着する旅行者がボトルネックを作ります。ID収集、支払い、ベッド割り当てを手動で行うと時間がかかりすぎます。' },
    ],
    solutionFeatures: [
      { title: 'ベッドレベルのルームボード', description: 'すべてのドミトリーの各ベッドを表示するビジュアルグリッド。個別のベッドをドラッグ＆ドロップで割り当て、交換、ブロック。', highlight: 'ベッドレベル' },
      { title: '高速チェックイン', description: 'ID スキャン、データ自動入力、支払い回収、ベッド割り当てを3分以内で。' },
      { title: 'ドミトリーグループ予約', description: 'ドミトリー全体または複数ベッドをグループ向けに予約。ルームリストとグループ請求を管理。' },
      { title: 'OTA同期と料金均一性', description: 'Hostelworld、Booking.comなど100以上のチャネルとの双方向同期。料金と空室状況の即時更新。' },
      { title: '基本カフェPOS', description: '共有エリアのカフェやバー用のシンプルなPOS。料金は自動的にゲストアカウントに追加。' },
      { title: 'クイックレポート', description: '日次の稼働率、収益、チェックアウトレポート。ホステルを効率的に管理するために必要な数字。' },
      { title: 'AI料金設定＆予測', description: 'AIが需要、季節性、競合料金に基づきベッド単価を設定。30/60/90日の需要予測がスタッフ計画とプロモーション企画を支援。' },
    ],
    caseStudy: { challenge: '20ドミトリー80ベッドのホステル。Excelでベッド追跡、予約の60%が高手数料のOTA経由、ピーク時のチェックイン待ち10分。', solution: 'HotelStack.ioをベッドレベルボード、高速チェックイン、OTAチャネル同期、直接予約ページで導入。', results: ['平均チェックイン時間を10分から3分未満に短縮', '直接予約が6ヶ月で40%から60%に増加', '20ドミトリー80ベッドすべてを一つのダッシュボードから管理', 'ダイナミック料金管理で平均稼働率90%達成'] },
    statsLabels: ['チェックイン時間', '直接予約', '管理ベッド数', '平均稼働率'],
    testimonial: { quote: 'ついにホステルが単なる小さなホテルではないと理解するソフトウェアが登場しました。ベッドレベルの管理がすべてを変えました。フロントチームは20人のグループを数分で処理できます。', role: '創業者', hotel: 'Nomad Social Hostel、バルセロナ' },
    faqItems: [
      { question: 'HotelStack.ioはベッドレベルの在庫をサポートしますか？', answer: 'はい。ルームボードはドミトリーのベッド管理とプライベートルームの部屋管理の両方をサポートします。' },
      { question: 'Hostelworldやホステル向けOTAと連携しますか？', answer: 'はい。Hostelworld、Booking.com、HostelBookersなど100以上のOTAチャネルと統合しています。' },
      { question: '高速チェックインはどのくらい速いですか？', answer: 'ゲスト1人あたり3分未満。IDスキャン、自動入力、支払い処理、ベッド割り当て。' },
      { question: 'ホステル向けの手頃なプランはありますか？', answer: 'はい。月49ドルからのプランで最大50室またはベッドユニットをサポート。地域別価格あり。' },
    ],
    relatedIndustries: [
      { label: '小規模ホテル', description: 'バジェット宿泊施設向けクラウドPMS' },
      { label: 'ブティックホテル', description: 'パーソナライズされたゲスト体験' },
      { label: 'リゾート', description: 'F&B＆多様なアクティビティ管理' },
      { label: 'ホテルチェーン', description: '集中型マルチプロパティ管理' },
    ],
  },
  'hotel-software-for-hotel-chains': {
    propertyType: 'ホテルチェーン運営者',
    heroTitle: '成長するチェーンのためのマルチプロパティホテルソフトウェア',
    heroSubtitle: 'AI搭載マルチプロパティ管理。施設横断AI収益インテリジェンス、集中AIダイナミックプライシング、ポートフォリオ全体の需要予測、チェーン全施設でのAI異常検出。',
    painPoints: [
      { title: '施設ごとに異なるシステム', description: '各施設が異なるソフトウェアを使用すると、統一ビューなし、データ形式の不一致、高額なライセンスが発生します。' },
      { title: '集中データなし', description: '統合レポートなしではチェーンマネージャーは手探り状態。施設間のパフォーマンス比較に手動でのデータ集約が必要。' },
      { title: '一貫性のない料金設定', description: '集中料金管理なしでは、施設がチャネル間で矛盾する料金を設定し、ブランドの信頼性を損ないます。' },
      { title: 'あらゆる場所で重複作業', description: '料金変更、ポリシー更新、ブランドガイドラインを各施設で手動で複製する必要があります。' },
    ],
    solutionFeatures: [
      { title: 'チェーン管理ダッシュボード', description: 'すべての施設の概要表示。各拠点の稼働率、収益、運営指標を一画面で。', highlight: '集中管理' },
      { title: 'クロスプロパティ分析', description: '施設間でRevPAR、ADR、稼働率、ゲスト満足度を比較。トップパフォーマーと注意が必要な施設を特定。' },
      { title: '集中料金管理', description: '一つのインターフェースからすべての施設とチャネルに料金を設定・配信。ブランドレベルの料金均一性を保証。' },
      { title: 'ロールベースのスタッフアクセス', description: 'チェーン、施設、部門レベルのロール。ポートフォリオ全体で誰が何を見るかの詳細な制御。' },
      { title: '統合コンプライアンス', description: '地域別税務報告、ゲスト登録、統合監査証跡。チェーン全体のワンクリックコンプライアンスレポート。' },
      { title: 'ブランド設定', description: 'すべての施設でブランド基準を定義・適用。統一された予約ページ、メールテンプレート、ゲストコミュニケーション。' },
      { title: '施設横断AIインテリジェンス', description: 'AI収益インテリジェンスが全施設のパフォーマンスを比較。ポートフォリオ全体の需要予測、集中AI料金設定、異常検出がゲストへの影響前に警告。' },
    ],
    caseStudy: { challenge: '5カ国12施設の成長中のチェーン。各施設が異なるPMSを使用。統合レポートなし、OTA料金の不一致、法人料金管理に手動更新が必要。', solution: '全12施設をHotelStack.io Enterprise Chainプランに移行。集中料金管理、統合ダッシュボード、統一ゲストCRM、ロールベースアクセス。', results: ['集中運営で管理コスト40%削減', '全チャネル・全施設の料金不一致を解消', '経営陣向けのリアルタイムクロスプロパティパフォーマンス可視化', 'ダウンタイムなしで30日間で全12施設をセットアップ'] },
    statsLabels: ['管理施設数', '管理コスト削減', '施設あたり追加', 'クロスプロパティデータ'],
    testimonial: { quote: 'HotelStack.io以前は月次パフォーマンスレポートの作成に3日かかっていました。今では一つのダッシュボードですべてを即座に確認できます。チェーンの管理方法が変わりました。', role: 'VP オペレーション', hotel: 'HotelStack Hotels & Resorts' },
    faqItems: [
      { question: '一つのアカウントで何施設まで管理できますか？', answer: '制限なし。Chainプランは集中管理で無制限の施設をサポート。追加ライセンス費用なしで新施設を追加。' },
      { question: 'すべての施設に集中料金を設定できますか？', answer: 'はい。集中料金管理により、一つのインターフェースからすべての施設に料金、制限、パッケージを配信できます。' },
      { question: 'マルチプロパティの統合レポートは取得できますか？', answer: 'はい。チェーンレベルのダッシュボードが統合および施設別の指標を表示。ワンクリックで統合レポートをエクスポート。' },
      { question: '複数施設の移行にはどのくらいかかりますか？', answer: '通常、週に3-4施設を移行します。12施設のチェーンは30日で完全に移行可能です。' },
    ],
    relatedIndustries: [
      { label: 'リゾート', description: '複雑なマルチアウトレットリゾート運営' },
      { label: 'ブティックホテル', description: 'カスタムブランド管理' },
      { label: '小規模ホテル', description: '個別施設向け手頃なPMS' },
      { label: 'ホステル', description: 'ベッドレベルのバジェット管理' },
    ],
  },
};
